import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatMoney } from '@belong/common';
import { Button, Divider, Text, Toast } from '@belong/ui';
import {
  MaintenanceQuoteConsentStatus,
  MaintenanceQuoteConsumerModel,
  MaintenanceQuoteRejectionReason,
  QuoteTypeOfWorkLabels,
} from 'maintenance/types/quote-centric';
import { updateQuoteCentricConsent } from 'store/redux/maintenance/actions';
import clsx from 'clsx';
import { QUOTE_APPROVAL_STATUS_STYLES } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import { isQuoteRejected } from 'maintenance/utils';
import { QuoteQuestionsModal } from './QuoteQuestionsModal';
import {
  AssignedProSection,
  LEADING,
  QuoteScopeOfWorkSections,
} from './QuoteScopeOfWorkSections';

type QuoteDetailsProps = {
  maintenanceUniqueId: string;
  quote: MaintenanceQuoteConsumerModel;
  details: NonNullable<MaintenanceQuoteConsumerModel['details']>;
  onQuotesChange: () => Promise<void>;
  hasMultipleQuotes?: boolean;
};

export const QuoteDetails = ({
  maintenanceUniqueId,
  quote,
  details,
  onQuotesChange,
  hasMultipleQuotes,
}: QuoteDetailsProps) => {
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const toastMessage = useRef('');

  const canDecide =
    !isQuoteRejected(quote) && quote.myApproval?.consentStatus === 'Required';

  const submitConsent = async (
    payload: {
      isApproved: boolean;
      rejectionReason?: MaintenanceQuoteRejectionReason;
      notes?: string;
    },
    successMessage: string,
  ) => {
    try {
      setIsUpdating(true);
      await dispatch(
        updateQuoteCentricConsent(maintenanceUniqueId, quote.uniqueId, payload),
      );
      toastMessage.current = successMessage;
      setIsQuestionsModalOpen(false);
      setToastVisible(true);
      await onQuotesChange();
    } catch (e) {
      console.error(e);
      toastMessage.current = STRINGS['quote-submit-error'];
      setToastVisible(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApprove = () =>
    submitConsent({ isApproved: true }, STRINGS['quote-approved']);

  const handleQuestionsSubmit = (
    rejectionReason: MaintenanceQuoteRejectionReason,
    notes?: string,
  ) =>
    submitConsent(
      { isApproved: false, rejectionReason, notes },
      STRINGS['quote-response-submitted'],
    );

  const myParty = quote.myApproval?.party ?? null;

  // A discount is stored as a negative-value `Discount` cost item. The total is
  // already net of it; surface it as its own line so the savings are visible.
  const discountItem = quote.costItems.find(
    (item) => item.type === 'Discount' && item.cost < 0,
  );
  const discountAmount = discountItem ? Math.abs(discountItem.cost) : 0;

  const isRejectedByStatus =
    isQuoteRejected(quote) &&
    !quote.approvals.some((a) => a.consentStatus === 'Rejected');

  const approvals: {
    party?: string;
    consentStatus: Exclude<MaintenanceQuoteConsentStatus, 'NotRequired'>;
  }[] = isRejectedByStatus
    ? [{ consentStatus: 'Rejected' }]
    : quote.approvals
        .filter(
          (
            a,
          ): a is typeof a & {
            consentStatus: Exclude<
              MaintenanceQuoteConsentStatus,
              'NotRequired'
            >;
          } => a.party !== 'Belong' && a.consentStatus !== 'NotRequired',
        )
        .sort(
          (a, b) => Number(a.party === myParty) - Number(b.party === myParty),
        );

  const displayApprovals =
    approvals.length > 1
      ? approvals
      : approvals.filter((a) => a.consentStatus !== 'Required');

  return (
    <div>
      <div
        className={
          hasMultipleQuotes
            ? 'p-sm'
            : 'rounded-t border border-solid border-gray bg-light-gray px-md pt-md pb-xl'
        }
      >
        <div className="flex items-start justify-between gap-sm">
          <Text fontWeight="semibold">
            {quote.title}
            {quote.typeOfWork
              ? ` • ${QuoteTypeOfWorkLabels[quote.typeOfWork]}`
              : ''}
          </Text>
        </div>

        {displayApprovals.length > 0 && (
          <div className="mt-xs flex flex-wrap gap-x-sm gap-y-2xs">
            {displayApprovals.map((a) => {
              const style = QUOTE_APPROVAL_STATUS_STYLES[a.consentStatus];
              const { Icon } = style;
              return (
                <div
                  key={a.party ?? a.consentStatus}
                  className={clsx('flex items-center gap-2xs', style.className)}
                >
                  <Icon className="shrink-0" width={12} height={12} />
                  <Text fontWeight="semibold" variant="p1">
                    {style.label(a.party !== myParty ? a.party : undefined)}
                  </Text>
                </div>
              );
            })}
          </div>
        )}

        <QuoteScopeOfWorkSections details={details} />

        {quote.assignedPro?.name && (
          <AssignedProSection name={quote.assignedPro.name} className="mt-xl" />
        )}

        <Divider borderStyle="dashed" className="mb-sm mt-xl" />

        <div className="flex items-center justify-between">
          <Text fontWeight="semibold" variant="h3" className={LEADING}>
            {STRINGS['quote-total']}
          </Text>
          <div className="flex flex-col items-end gap-xs">
            <Text fontWeight="semibold" variant="h3" className={LEADING}>
              {quote.totalAmount > 0
                ? formatMoney(quote.totalAmount)
                : STRINGS['quote-free']}
            </Text>
            {discountAmount > 0 && (
              <Text
                fontWeight="semibold"
                variant="p1"
                className={clsx('uppercase', LEADING)}
              >
                <span className="text-dark-gray line-through">
                  {formatMoney(quote.totalAmount + discountAmount)}
                </span>
                <span className="text-green">
                  {` ( -${formatMoney(discountAmount)} ${STRINGS['quote-discount']})`}
                </span>
              </Text>
            )}
          </div>
        </div>
      </div>
      {canDecide && (
        <>
          <div
            className={clsx(
              'mt-lg flex items-center justify-between gap-sm bg-dark-navy p-sm',
              hasMultipleQuotes ? 'rounded-b' : 'rounded',
            )}
          >
            <Button
              variant="text"
              buttonType="light"
              onClick={() => setIsQuestionsModalOpen(true)}
              disabled={isUpdating}
            >
              {STRINGS['quote-questions']}
            </Button>
            <Button size="small" onClick={handleApprove} disabled={isUpdating}>
              {STRINGS['quote-approve']}
            </Button>
          </div>
          <QuoteQuestionsModal
            isOpen={isQuestionsModalOpen}
            isSubmitting={isUpdating}
            onDismiss={() => setIsQuestionsModalOpen(false)}
            onSubmit={handleQuestionsSubmit}
          />
        </>
      )}

      <Toast
        autocloseDuration={5000}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      >
        {toastMessage.current}
      </Toast>
    </div>
  );
};
